import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Input, Select, PrimaryButton, DangerButton } from "@/components/admin/AdminUI";
import { GripVertical } from "lucide-react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export default function AdminNavBuilder() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["admin-nav"], queryFn: async () => (await api.get("/admin/navigation")).data });
  const { data: cmsPages = [] } = useQuery({ queryKey: ["cms-list"], queryFn: async () => (await api.get("/admin/cms-pages")).data });

  const header = useMemo(
    () => items.filter((i) => (i.placement || "header") === "header").sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [items]
  );
  const footer = useMemo(
    () => items.filter((i) => i.placement === "footer").sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [items]
  );

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["admin-nav"] });
    qc.invalidateQueries({ queryKey: ["nav"] });
  }

  async function add(placement) {
    const existing = items.filter((i) => (i.placement || "header") === placement);
    await api.post("/admin/navigation", {
      label: "New item",
      placement,
      link_type: "static_page",
      static_page_key: "home",
      sort_order: (existing.length + 1) * 10,
    });
    invalidate();
  }

  async function update(id, payload) {
    await api.patch(`/admin/navigation/${id}`, payload);
    invalidate();
  }

  async function remove(id) {
    if (!window.confirm("Remove this menu item?")) return;
    await api.delete(`/admin/navigation/${id}`);
    invalidate();
  }

  return (
    <div>
      <PageHeader
        title="Navigation"
        subtitle="Manage the public top menu and the footer links. Drag rows to reorder within each section."
      />

      <NavSection
        title="Top menu (header)"
        items={header}
        cmsPages={cmsPages}
        onAdd={() => add("header")}
        onUpdate={update}
        onRemove={remove}
        invalidate={invalidate}
        testid="header"
      />

      <div className="mt-8">
        <NavSection
          title="Footer links"
          items={footer}
          cmsPages={cmsPages}
          onAdd={() => add("footer")}
          onUpdate={update}
          onRemove={remove}
          invalidate={invalidate}
          testid="footer"
        />
      </div>
    </div>
  );
}

function NavSection({ title, items, cmsPages, onAdd, onUpdate, onRemove, invalidate, testid }) {
  const [order, setOrder] = useState([]);
  useEffect(() => { setOrder(items.map((i) => i.id)); }, [items]);

  async function persistOrder(newOrder) {
    setOrder(newOrder);
    await api.post("/admin/navigation/reorder", { ordered_ids: newOrder });
    invalidate();
  }

  const ordered = order.map((id) => items.find((i) => i.id === id)).filter(Boolean);

  return (
    <div data-testid={`nav-section-${testid}`}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm uppercase tracking-wider text-[#6C757D]">{title}</h2>
        <PrimaryButton onClick={onAdd} data-testid={`nav-add-${testid}`}>+ Add item</PrimaryButton>
      </div>
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={(e) => {
          const { active, over } = e;
          if (over && active.id !== over.id) {
            const oldIndex = order.indexOf(active.id);
            const newIndex = order.indexOf(over.id);
            persistOrder(arrayMove(order, oldIndex, newIndex));
          }
        }}
      >
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {ordered.length === 0 && (
              <Card><p className="text-sm text-[#6C757D]">No items yet. Click "Add item" to create one.</p></Card>
            )}
            {ordered.map((item) => (
              <NavRow key={item.id} item={item} cmsPages={cmsPages} onUpdate={onUpdate} onRemove={onRemove} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function NavRow({ item, cmsPages, onUpdate, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const [local, setLocal] = useState(item);
  useEffect(() => setLocal(item), [item]);

  function commit(payload) {
    onUpdate(item.id, payload);
  }

  return (
    <Card>
      <div ref={setNodeRef} style={style} className="grid sm:grid-cols-[24px_1fr_180px_1fr_140px_100px] gap-3 items-center">
        <button {...listeners} {...attributes} className="cursor-grab text-[#6C757D]"><GripVertical className="w-4 h-4" /></button>
        <Input value={local.label} onChange={(e) => setLocal((l) => ({ ...l, label: e.target.value }))} onBlur={(e) => e.target.value !== item.label && commit({ label: e.target.value })} />
        <Select value={local.link_type} onChange={(e) => { setLocal((l) => ({ ...l, link_type: e.target.value })); commit({ link_type: e.target.value }); }}>
          <option value="static_page">Static page</option>
          <option value="cms_page">CMS page</option>
          <option value="external_url">External URL</option>
        </Select>
        {local.link_type === "static_page" && (
          <Select value={local.static_page_key || ""} onChange={(e) => commit({ static_page_key: e.target.value })}>
            <option value="home">Home</option>
            <option value="brands">Brands</option>
            <option value="clothing">Clothing</option>
            <option value="materials_guide">Materials Guide</option>
            <option value="materials_certifications">Materials certifications</option>
            <option value="brand_certifications">Brand certifications</option>
            <option value="methodology">Methodology</option>
            <option value="contact">Contact</option>
          </Select>
        )}
        {local.link_type === "cms_page" && (
          <Select value={local.cms_page_id || ""} onChange={(e) => commit({ cms_page_id: e.target.value })}>
            <option value="">Select page…</option>
            {cmsPages.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </Select>
        )}
        {local.link_type === "external_url" && (
          <Input value={local.external_url || ""} onChange={(e) => setLocal((l) => ({ ...l, external_url: e.target.value }))} onBlur={(e) => e.target.value !== item.external_url && commit({ external_url: e.target.value })} placeholder="https://…" />
        )}
        <label className="text-sm inline-flex items-center gap-2"><input type="checkbox" checked={!!local.is_public} onChange={(e) => commit({ is_public: e.target.checked })} /> Public</label>
        <DangerButton onClick={() => onRemove(item.id)}>Remove</DangerButton>
      </div>
    </Card>
  );
}
